import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
    FiSearch, FiDownload, FiEye, FiClock, FiMapPin, 
    FiUser, FiMail, FiPhone, FiTruck, FiTag, FiDollarSign,
    FiRefreshCw, FiSend, FiUsers, FiAward, FiCamera
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

// API configuration
const API_URL = process.env.REACT_APP_API_URL || '/api';

const Container = styled.div`
    padding: ${props => props.theme.spacing.xl};
    max-width: 1400px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
    font-size: ${props => props.theme.fontSizes.xxl};
    font-weight: 700;
    color: ${props => props.theme.colors.gray[800]};
`;

const StatsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled.div`
    background: white;
    padding: ${props => props.theme.spacing.lg};
    border-radius: ${props => props.theme.borders.radius.lg};
    box-shadow: ${props => props.theme.shadows.sm};
    text-align: center;
`;

const StatNumber = styled.div`
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatLabel = styled.div`
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.gray[600]};
`;

const FiltersContainer = styled.div`
    background: white;
    padding: ${props => props.theme.spacing.lg};
    border-radius: ${props => props.theme.borders.radius.lg};
    box-shadow: ${props => props.theme.shadows.sm};
    margin-bottom: ${props => props.theme.spacing.xl};
    display: flex;
    gap: ${props => props.theme.spacing.md};
    align-items: center;
    flex-wrap: wrap;
`;

const SearchInput = styled.input`
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    border: 1px solid ${props => props.theme.colors.gray[300]};
    border-radius: ${props => props.theme.borders.radius.md};
    font-size: ${props => props.theme.fontSizes.sm};
    flex: 1;
    min-width: 200px;

    &:focus {
        outline: none;
        border-color: ${props => props.theme.colors.primary};
    }
`;

const Select = styled.select`
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    border: 1px solid ${props => props.theme.colors.gray[300]};
    border-radius: ${props => props.theme.borders.radius.md};
    font-size: ${props => props.theme.fontSizes.sm};
    background: white;

    &:focus {
        outline: none;
        border-color: ${props => props.theme.colors.primary};
    }
`;

const ExportButton = styled.button`
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    background: ${props => props.theme.colors.success};
    color: white;
    border: none;
    border-radius: ${props => props.theme.borders.radius.md};
    cursor: pointer;
    font-size: ${props => props.theme.fontSizes.sm};
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.theme.colors.gray[800]};
    }
`;

const TicketsTable = styled.div`
    background: white;
    border-radius: ${props => props.theme.borders.radius.lg};
    box-shadow: ${props => props.theme.shadows.sm};
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 150px 100px;
    padding: ${props => props.theme.spacing.md};
    background: ${props => props.theme.colors.gray[50]};
    font-weight: 600;
    color: ${props => props.theme.colors.gray[700]};
    border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
`;

const TicketRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 150px 100px;
    padding: ${props => props.theme.spacing.md};
    border-bottom: 1px solid ${props => props.theme.colors.gray[100]};
    transition: background-color 0.2s ease;

    &:hover {
        background: ${props => props.theme.colors.gray[50]};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const AttendeeCard = styled.div`
    background: white;
    border-radius: ${props => props.theme.borders.radius.lg};
    box-shadow: ${props => props.theme.shadows.sm};
    margin-bottom: ${props => props.theme.spacing.lg};
    overflow: hidden;
    border: 1px solid ${props => props.theme.colors.gray[200]};
`;

const AttendeeHeader = styled.div`
    background: ${props => props.theme.colors.gray[50]};
    padding: ${props => props.theme.spacing.lg};
    border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
`;

const AttendeeBody = styled.div`
    padding: ${props => props.theme.spacing.lg};
`;

const AttendeeInfo = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.lg};

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const InfoSection = styled.div`
    h4 {
        margin: 0 0 ${props => props.theme.spacing.sm} 0;
        font-size: ${props => props.theme.fontSizes.md};
        font-weight: 600;
        color: ${props => props.theme.colors.gray[800]};
        display: flex;
        align-items: center;
        gap: ${props => props.theme.spacing.xs};
    }
`;

const InfoItem = styled.div`
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.gray[600]};
    margin-bottom: ${props => props.theme.spacing.xs};
    line-height: 1.4;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: ${props => props.theme.spacing.sm};
    margin-top: ${props => props.theme.spacing.lg};
    padding-top: ${props => props.theme.spacing.lg};
    border-top: 1px solid ${props => props.theme.colors.gray[200]};
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.xs};
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    border: 1px solid;
    border-radius: ${props => props.theme.borders.radius.md};
    cursor: pointer;
    font-size: ${props => props.theme.fontSizes.sm};
    font-weight: 500;
    transition: all 0.2s ease;

    ${props => props.variant === 'refund' && `
        border-color: ${props.theme.colors.danger};
        color: ${props.theme.colors.danger};
        background: white;

        &:hover {
            background: ${props.theme.colors.danger};
            color: white;
        }
    `}

    ${props => props.variant === 'email' && `
        border-color: ${props.theme.colors.primary};
        color: ${props.theme.colors.primary};
        background: white;

        &:hover {
            background: ${props.theme.colors.primary};
            color: white;
        }
    `}
`;

const AddOnList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${props => props.theme.spacing.xs};
    margin-top: ${props => props.theme.spacing.xs};
`;

const AddOnTag = styled.span`
    background: ${props => props.theme.colors.primary}20;
    color: ${props => props.theme.colors.primary};
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    border-radius: ${props => props.theme.borders.radius.sm};
    font-size: ${props => props.theme.fontSizes.xs};
    font-weight: 500;

`;

const StatusBadge = styled.span.withConfig({
    shouldForwardProp: (prop) => !['status'].includes(prop)
})`
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    border-radius: ${props => props.theme.borders.radius.sm};
    font-size: ${props => props.theme.fontSizes.xs};
    font-weight: 600;
    text-transform: uppercase;
    background: ${props => {
        switch (props.status) {
            case 'completed': return props.theme.colors.success;
            case 'pending': return props.theme.colors.warning;
            case 'cancelled': return props.theme.colors.danger;
            default: return props.theme.colors.gray[500];
        }
    }};
    color: white;
`;

const LoadingSpinner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${props => props.theme.spacing.xl};
    font-size: ${props => props.theme.fontSizes.lg};
    color: ${props => props.theme.colors.gray[600]};
`;

const EmptyState = styled.div`
    text-align: center;
    padding: ${props => props.theme.spacing.xl};
    color: ${props => props.theme.colors.gray[600]};

    h3 {
        margin-bottom: ${props => props.theme.spacing.md};
        color: ${props => props.theme.colors.gray[800]};
    }
`;

const TopNavigationBar = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: ${props => props.theme.spacing.xl};
    background: white;
    border-radius: ${props => props.theme.borders.radius.lg};
    box-shadow: ${props => props.theme.shadows.sm};
    padding: ${props => props.theme.spacing.md};
`;

const NavTab = styled.button`
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
    background: ${props => props.active ? props.theme.colors.primary : 'transparent'};
    color: ${props => props.active ? 'white' : props.theme.colors.gray[600]};
    border: none;
    border-radius: ${props => props.theme.borders.radius.md};
    cursor: pointer;
    font-size: ${props => props.theme.fontSizes.sm};
    font-weight: 500;
    transition: all 0.2s ease;
    margin: 0 ${props => props.theme.spacing.xs};

    &:hover {
        background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.gray[100]};
        color: ${props => props.active ? 'white' : props.theme.colors.gray[800]};
    }
`;

const Tickets = () => {
    const { token } = useAuth();
    const [attendees, setAttendees] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEvent, setSelectedEvent] = useState('');
    const [activeTab, setActiveTab] = useState('tickets-by-user');
    const [revenue, setRevenue] = useState({ total: 0, currency: 'AED' });
    const [stats, setStats] = useState({
        totalAttendees: 0,
        activeAttendees: 0,
        totalRevenue: 0,
        recentAttendees: 0
    });

    useEffect(() => {
        fetchAttendees();
        fetchEvents();
        fetchStats();
        fetchRevenue();
    }, []);

    const fetchAttendees = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/admin/attendees`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setAttendees(data.attendees || []);
                console.log('✅ Attendees loaded:', data.attendees?.length);
            } else {
                console.error('❌ Failed to fetch attendees:', response.status);
            }
        } catch (error) {
            console.error('❌ Error fetching attendees:', error);
        } finally {
            setLoading(false);
        }
    };
    const fetchEvents = async () => {
        try {
            const response = await fetch(`${API_URL}/events?status=active&limit=100`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setEvents(data.events || []);
            }
        } catch (error) {
            console.error('❌ Error fetching events:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await fetch(`${API_URL}/admin/attendees/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setStats(data.stats || {});
            }
        } catch (error) {
            console.error('❌ Error fetching stats:', error);
        }
    };

    const fetchRevenue = async () => {
        try {
            const response = await fetch(`${API_URL}/admin/dashboard/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setRevenue(data.stats?.revenue || { total: 0, currency: 'AED' });
            }
        } catch (error) {
            console.error('❌ Error fetching revenue:', error);
        }
    };

    const filteredAttendees = attendees.filter(attendee => {
        const matchesSearch = searchTerm === '' || 
            attendee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            attendee.purchaserInfo?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            attendee.purchaserInfo?.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            attendee.eventName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesEvent = selectedEvent === '' || attendee.eventId === selectedEvent;

        return matchesSearch && matchesEvent;
    });

    const exportAttendees = () => {
        const csvContent = "data:text/csv;charset=utf-8," 
            + "Attendee Name,Age,Purchaser Name,Purchaser Email,Purchaser Phone,Event,Date,Ticket Type,Price,Currency,Add-ons,Payment ID,Status,Transportation\n"
            + filteredAttendees.map(attendee => {
                const addOnNames = attendee.addOns ? attendee.addOns.map(a => a.name).join('; ') : '';
                const hasTransportation = attendee.transportationDetails ? 'Yes' : 'No';
                
                return [
                    attendee.name,
                    attendee.age,
                    attendee.purchaserInfo?.name || 'N/A',
                    attendee.purchaserInfo?.email || 'N/A',
                    attendee.purchaserInfo?.phone || 'N/A',
                    attendee.eventName,
                    attendee.eventDate ? new Date(attendee.eventDate).toLocaleDateString() : 'N/A',
                    attendee.ticketTypeName,
                    attendee.ticketPrice,
                    attendee.currency || 'AED',
                    addOnNames,
                    attendee.paymentId,
                    attendee.status,
                    hasTransportation
                ].join(',');
            }).join('\n');
            
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `attendees-export-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return timeString || 'N/A';
        }
    };

    const handleRefund = async (attendeeId) => {
        // TODO: Implement refund functionality
        console.log(`Refund requested for attendee: ${attendeeId}`);
        alert('Refund functionality will be implemented');
    };

    const handleSendEmail = async (attendeeId) => {
        // TODO: Implement email sending functionality
        console.log(`Send email requested for attendee: ${attendeeId}`);
        alert('Email sending functionality will be implemented');
    };

    if (loading) {
        return (
            <Container>
                <LoadingSpinner>Loading tickets...</LoadingSpinner>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <Title>Tickets Management</Title>
                <ExportButton onClick={exportAttendees}>
                    <FiDownload />
                    Export CSV
                </ExportButton>
            </Header>

            {/* Top Navigation Bar */}
            <TopNavigationBar>
                <NavTab 
                    active={activeTab === 'tickets-by-user'} 
                    onClick={() => setActiveTab('tickets-by-user')}
                >
                    <FiUsers />
                    Tickets by User
                </NavTab>
                <NavTab 
                    active={activeTab === 'badge-allocation'} 
                    onClick={() => setActiveTab('badge-allocation')}
                >
                    <FiAward />
                    Badge Allocation
                </NavTab>
                <NavTab 
                    active={activeTab === 'scanners-management'} 
                    onClick={() => setActiveTab('scanners-management')}
                >
                    <FiCamera />
                    Scanners Management
                </NavTab>
            </TopNavigationBar>

            {/* Tab Content */}
            {activeTab === 'tickets-by-user' && (
                <>
                    {/* Statistics */}
                    <StatsContainer>
                        <StatCard>
                            <StatNumber>{stats.totalAttendees}</StatNumber>
                            <StatLabel>Total Attendees</StatLabel>
                        </StatCard>
                        <StatCard>
                            <StatNumber>{stats.activeAttendees}</StatNumber>
                            <StatLabel>Active Attendees</StatLabel>
                        </StatCard>
                        <StatCard>
                            <StatNumber>{stats.recentAttendees}</StatNumber>
                            <StatLabel>Recent Bookings</StatLabel>
                        </StatCard>
                        <StatCard>
                            <StatNumber>{attendees.filter(a => a.transportationDetails).length}</StatNumber>
                            <StatLabel>Transportation Requests</StatLabel>
                        </StatCard>
                        <StatCard>
                            <StatNumber>{revenue.total.toLocaleString()} {revenue.currency}</StatNumber>
                            <StatLabel>Total Revenue</StatLabel>
                        </StatCard>
                    </StatsContainer>

                    {/* Filters */}
            <FiltersContainer>
                <FiSearch style={{ color: '#9CA3AF' }} />
                <SearchInput
                    type="text"
                    placeholder="Search by attendee name, purchaser email, event..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                >
                    <option value="">All Events</option>
                    {events.map(event => (
                        <option key={event._id} value={event._id}>
                            {event.name}
                        </option>
                    ))}
                </Select>
            </FiltersContainer>

            {/* Top Navigation Bar */}
            <TopNavigationBar>
                <NavTab active>
                    <FiEye />
                    All Attendees
                </NavTab>
                <NavTab>
                    <FiClock />
                    Upcoming
                </NavTab>
                <NavTab>
                    <FiMapPin />
                    Checked In
                </NavTab>
                <NavTab>
                    <FiTruck />
                    Transportation
                </NavTab>
            </TopNavigationBar>

            {/* Attendees Display */}
            {filteredAttendees.length === 0 ? (
                <EmptyState>
                    <h3>No Attendees Found</h3>
                    <p>No attendees match your current filters.</p>
                </EmptyState>
            ) : (
                <div>
                    {filteredAttendees.map((attendee) => (
                        <AttendeeCard key={attendee._id}>
                            <AttendeeHeader>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            {attendee.name} ({attendee.age} years)
                                        </h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.9rem', opacity: 0.9 }}>
                                            <span><FiClock style={{ marginRight: '0.25rem' }} />{formatDate(attendee.eventDate)} at {formatTime(attendee.eventTime)}</span>
                                            <span><FiMapPin style={{ marginRight: '0.25rem' }} />{attendee.eventVenue}</span>
                                            <span><FiUser style={{ marginRight: '0.25rem' }} />ID: {attendee.attendeeId}</span>
                                        </div>
                                    </div>
                                    <StatusBadge status={attendee.status}>
                                        {attendee.status}
                                    </StatusBadge>
                                </div>
                            </AttendeeHeader>

                            <AttendeeBody>
                                <AttendeeInfo>
                                    {/* Attendee Information */}
                                    <InfoSection>
                                        <h4><FiUser /> Attendee Details</h4>
                                        <InfoItem>Name: {attendee.name}</InfoItem>
                                        <InfoItem>Age: {attendee.age}</InfoItem>
                                        <InfoItem>Event: {attendee.eventName}</InfoItem>
                                        <InfoItem>Ticket Type: {attendee.ticketTypeName}</InfoItem>
                                        <InfoItem>Price: {attendee.ticketPrice} {attendee.currency}</InfoItem>
                                        <InfoItem>Status: {attendee.status}</InfoItem>
                                        {attendee.checkedIn && (
                                            <InfoItem>✅ Checked In: {formatDate(attendee.checkedInAt)}</InfoItem>
                                        )}
                                    </InfoSection>

                                    {/* Purchaser Information */}
                                    <InfoSection>
                                        <h4><FiMail /> Purchaser Details</h4>
                                        <InfoItem>Name: {attendee.purchaserInfo?.name || 'N/A'}</InfoItem>
                                        <InfoItem>Email: {attendee.purchaserInfo?.email || 'N/A'}</InfoItem>
                                        <InfoItem>Phone: {attendee.purchaserInfo?.phone || 'N/A'}</InfoItem>
                                        <InfoItem>User ID: {attendee.purchaserUserId || 'N/A'}</InfoItem>
                                    </InfoSection>

                                    {/* Payment & Transportation */}
                                    <InfoSection>
                                        <h4><FiDollarSign /> Payment & Transport</h4>
                                        <InfoItem>Payment ID: {attendee.paymentId || 'N/A'}</InfoItem>
                                        <InfoItem>Payment Status: {attendee.paymentStatus || 'N/A'}</InfoItem>
                                        <InfoItem>Total Amount: {attendee.paymentAmount || 'N/A'} {attendee.currency}</InfoItem>
                                        {attendee.transportationDetails ? (
                                            <>
                                                <InfoItem><FiTruck /> Transportation Requested</InfoItem>
                                                <InfoItem>Pickup: {attendee.transportationDetails.pickupLocation}</InfoItem>
                                                <InfoItem>Dropoff: {attendee.transportationDetails.dropoffLocation}</InfoItem>
                                                {attendee.transportationDetails.additionalRequests && (
                                                    <InfoItem>Notes: {attendee.transportationDetails.additionalRequests}</InfoItem>
                                                )}
                                            </>
                                        ) : (
                                            <InfoItem>No transportation requested</InfoItem>
                                        )}
                                    </InfoSection>
                                </AttendeeInfo>

                                {/* Add-ons */}
                                {attendee.addOns && attendee.addOns.length > 0 && (
                                    <InfoSection style={{ marginTop: '1rem' }}>
                                        <h4><FiTag /> Add-ons</h4>
                                        <AddOnList>
                                            {attendee.addOns.map((addon, index) => (
                                                <AddOnTag key={index}>
                                                    {addon.name} - {addon.price} {attendee.currency} x{addon.quantity}
                                                    {addon.associationType && ` (${addon.associationType})`}
                                                </AddOnTag>
                                            ))}
                                        </AddOnList>
                                    </InfoSection>
                                )}

                                {/* Action Buttons */}
                                <ActionButtons>
                                    <ActionButton 
                                        variant="refund" 
                                        onClick={() => handleRefund(attendee.attendeeId)}
                                    >
                                        <FiRefreshCw />
                                        Refund
                                    </ActionButton>
                                    <ActionButton 
                                        variant="email" 
                                        onClick={() => handleSendEmail(attendee.attendeeId)}
                                    >
                                        <FiSend />
                                        Send Email
                                    </ActionButton>
                                </ActionButtons>
                            </AttendeeBody>
                        </AttendeeCard>
                    ))}
                </div>
            )}
            </>
            )}

            {/* Badge Allocation Tab */}
            {activeTab === 'badge-allocation' && (
                <div style={{ padding: '2rem', textAlign: 'center', background: 'white', borderRadius: '8px', marginTop: '2rem' }}>
                    <h3>Badge Allocation</h3>
                    <p>Badge allocation functionality coming soon...</p>
                </div>
            )}

            {/* Scanners Management Tab */}
            {activeTab === 'scanners-management' && (
                <div style={{ padding: '2rem', textAlign: 'center', background: 'white', borderRadius: '8px', marginTop: '2rem' }}>
                    <h3>Scanners Management</h3>
                    <p>Scanner management functionality coming soon...</p>
                </div>
            )}
        </Container>
    );
};

export default Tickets;
